<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import inboxesAPI from 'dashboard/api/inboxes';
import SettingsSection from 'dashboard/components/SettingsSection.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

// Read-only status display.
const syncStatus = ref(null);
const syncError = ref(null);
const scheduledAt = ref(null);

// Poll active syncs more often than scheduled ones.
const SYNCING_POLL_INTERVAL_MS = 10000;
const SCHEDULED_POLL_INTERVAL_MS = 5 * 60 * 1000;
let pollTimeoutId = null;

const STATE_CONFIG = {
  syncing: {
    icon: 'i-lucide-loader-circle',
    iconClass: 'animate-spin text-n-slate-11',
    labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_HISTORY_SYNC_SYNCING',
  },
  synced: {
    icon: 'i-lucide-check-circle-2',
    iconClass: 'text-n-teal-11',
    labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_HISTORY_SYNC_SYNCED',
  },
  not_synced: {
    icon: 'i-lucide-circle-alert',
    iconClass: 'text-n-amber-11',
    labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_HISTORY_SYNC_NOT_SYNCED',
  },
  scheduled: {
    icon: 'i-lucide-clock',
    iconClass: 'text-n-slate-11',
    labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_HISTORY_SYNC_SCHEDULED',
  },
};

const currentState = computed(() => STATE_CONFIG[syncStatus.value] || null);

const formattedScheduledAt = computed(() =>
  scheduledAt.value ? new Date(scheduledAt.value).toLocaleString() : ''
);

const stateLabel = computed(() => {
  if (!currentState.value) return '';
  return t(currentState.value.labelKey, { date: formattedScheduledAt.value });
});

const fetchStatus = async () => {
  try {
    const response = await inboxesAPI.getWhatsappHistorySyncStatus(
      props.inbox.id
    );
    syncStatus.value = response.data.status;
    syncError.value = response.data.last_error;
    scheduledAt.value = response.data.scheduled_at;
  } catch (error) {
    syncStatus.value = null;
    syncError.value = null;
    scheduledAt.value = null;
  }
};

const stopPolling = () => {
  if (pollTimeoutId) {
    clearTimeout(pollTimeoutId);
    pollTimeoutId = null;
  }
};

// Poll while syncing or scheduled.
const POLLING_STATUSES = ['syncing', 'scheduled'];

const scheduleNextPoll = () => {
  stopPolling();
  if (!POLLING_STATUSES.includes(syncStatus.value)) return;

  const interval =
    syncStatus.value === 'scheduled'
      ? SCHEDULED_POLL_INTERVAL_MS
      : SYNCING_POLL_INTERVAL_MS;

  pollTimeoutId = setTimeout(async () => {
    await fetchStatus();
    scheduleNextPoll();
  }, interval);
};

onMounted(async () => {
  await fetchStatus();
  scheduleNextPoll();
});

onUnmounted(stopPolling);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <SettingsSection
    v-if="currentState"
    :title="t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_HISTORY_SYNC_TITLE')"
  >
    <div class="flex items-center gap-2 text-sm text-n-slate-12">
      <span
        :class="[currentState.icon, currentState.iconClass]"
        class="size-4 shrink-0"
      />
      <span>{{ stateLabel }}</span>
    </div>
    <p v-if="syncStatus === 'scheduled'" class="text-xs text-n-slate-11 mt-1">
      {{
        t(
          'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_HISTORY_SYNC_SCHEDULED_DESCRIPTION'
        )
      }}
    </p>
    <p
      v-else-if="syncStatus === 'not_synced'"
      class="text-xs mt-1 text-n-slate-11"
    >
      {{
        syncError ||
        t(
          'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_HISTORY_SYNC_NOT_SYNCED_DESCRIPTION'
        )
      }}
    </p>
  </SettingsSection>
</template>
